import React, {useEffect} from 'react';
import AppDialog from './AppDialog';
import {SVG} from '../../constants/assetLists';
import AppButton from './AppButton';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import AppTextInput from './AppTextInput';
import {isMissingAny} from '../../utils';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {handleChangeUserPasswordService} from '../../services/users';
import {useSnackbar} from '../../context/SnackbarContextProvider';
import QUERY_KEYS from '../../constants/queryKeys';

type Props = {
  open: boolean;
  onClose: () => void;
};
const zodSchema = z
  .object({
    oldPassword: z.string().nonempty('שדה חובה!'),
    newPassword: z.string().nonempty('שדה חובה!').min(8, 'סיסמה חלשה'),
    againPassword: z.string().nonempty('שדה חובה!'),
  })
  .refine(data => data.newPassword === data.againPassword, {
    message: 'הסיסמה אינה תואמת.',
    path: ['againPassword'],
  })
  .refine(data => data.newPassword !== data.oldPassword, {
    message: 'הסיסמה החדשה חייבת להיות שונה מהסיסמה הישנה',
    path: ['newPassword'],
  });

export type FormValues = z.infer<typeof zodSchema>;
const initialStateShowPassword = {
  oldPassword: false,
  newPassword: false,
  againPassword: false,
};
function AppChangePasswordDialog({open, onClose}: Props) {
  const [showPassword, setShowPassword] = React.useState(
    initialStateShowPassword,
  );
  const [isNewPasswordFocused, setIsNewPasswordFocused] = React.useState(false);
  const {showSnackbar} = useSnackbar();
  const queryClient = useQueryClient();

  const {mutate: handleChangePassword, isPending: isPasswordChanging} =
    useMutation({
      mutationFn: (payload: {oldPassword: string; newPassword: string}) =>
        handleChangeUserPasswordService(payload),
      onSuccess: data => {
        showSnackbar({
          message: data?.message ?? 'רשימת פתיחת בר נוצרה',
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_SINGLE_USER],
        });
        onClose();
      },
      onError: errors => {
        showSnackbar({
          message: errors?.message || 'שינוי הסיסמה נכשל, נסה שוב.',
          type: 'error',
        });
      },
    });

  const method = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      againPassword: '',
    },
    resolver: zodResolver(zodSchema),
  });

  const {
    formState: {errors},
    control,
    watch,
    handleSubmit,
  } = method;

  const handleConfirm = (formData: FormValues) => {
    handleChangePassword({
      oldPassword: formData.oldPassword,
      newPassword: formData.newPassword,
    });
  };

  const oldPassword = watch('oldPassword');
  const newPassword = watch('newPassword');
  const againPassword = watch('againPassword');

  const isFairPassword = isMissingAny(newPassword);

  const labelClasses =
    'pointer-events-none !top-1/2 !transform !-translate-y-1/2 transition-all duration-200 group-focus-within:!top-1 group-focus-within:!text-[12px] group-focus-within:!translate-y-0';
  return (
    <AppDialog
      showModel={open}
      onClose={onClose}
      showCloseIcon={true}
      width={550}
      containerClasses="!py-0 !pt-4"
      titleContainerClasses="!gap-2"
      title="שינוי סיסמא"
      titleIcon={
        <img
          src={SVG.passwordLock}
          alt="icon"
          className="w-6 h-6 object-contain"
        />
      }>
      <div className="flex flex-col gap-3 mt-3 pb-3 px-10">
        <Controller
          name="oldPassword"
          control={control}
          render={({field}) => {
            return (
              <div className="flex items-center">
                <AppTextInput
                  type={showPassword.oldPassword ? 'text' : 'password'}
                  label="סיסמה ישנה"
                  labelClassName={field.value == '' ? labelClasses : ''}
                  required
                  // placeholder="שם קבוצה"
                  className="border-0 !pl-14"
                  {...field}
                  error={errors.oldPassword?.message || ''}
                  endIcon={
                    <img
                      src={
                        showPassword.oldPassword
                          ? SVG.eyeOnIcon
                          : SVG.eyeOffIcon
                      }
                      alt="password-strength"
                      className="h-7 w-7 mb-1"
                      onClick={() =>
                        setShowPassword(prev => ({
                          ...prev,
                          oldPassword: !prev.oldPassword,
                        }))
                      }
                    />
                  }
                />
              </div>
            );
          }}
        />
        <Controller
          name="newPassword"
          control={control}
          render={({field}) => {
            return (
              <div className="flex flex-col">
                <AppTextInput
                  type={showPassword.newPassword ? 'text' : 'password'}
                  label="סיסמה חדשה"
                  labelClassName={field.value == '' ? labelClasses : ''}
                  // placeholder="סיסמה חדשה"
                  required
                  className="border-0 !pl-14"
                  {...field}
                  error={errors.newPassword?.message || ''}
                  onFocus={() => setIsNewPasswordFocused(true)}
                  onBlur={e => {
                    setIsNewPasswordFocused(false);
                    field.onBlur();
                  }}
                  endIcon={
                    <img
                      src={
                        showPassword.newPassword
                          ? SVG.eyeOnIcon
                          : SVG.eyeOffIcon
                      }
                      alt="password-strength"
                      className="h-7 w-7 mb-1"
                      onClick={() =>
                        setShowPassword(prev => ({
                          ...prev,
                          newPassword: !prev.newPassword,
                        }))
                      }
                    />
                  }
                />
                {!errors.newPassword?.message &&
                  (field?.value as string)?.length > 7 && (
                    <p
                      className={`m-0 text-[12px] ${
                        isFairPassword ? 'text-orange-400' : 'text-green'
                      }`}>
                      {isFairPassword ? 'סיסמה הוגנת' : 'סיסמה חזקה'}
                    </p>
                  )}
                {!errors.newPassword?.message &&
                  !field.value &&
                  isNewPasswordFocused && (
                    <p className="m-0 text-[12px]">
                      מינימום 8 תווים המורכבים מאותיות גדולות ותחתונות, מספרים
                      וסמלים מיוחדים
                    </p>
                  )}
              </div>
            );
          }}
        />
        <Controller
          name="againPassword"
          control={control}
          render={({field}) => {
            return (
              <div className="flex items-center">
                <AppTextInput
                  type={showPassword.againPassword ? 'text' : 'password'}
                  label="הקלדת סיסמא חדשה שוב"
                  labelClassName={field.value == '' ? labelClasses : ''}
                  required
                  // placeholder="שם קבוצה"
                  className="border-0 !pl-14"
                  {...field}
                  error={errors.againPassword?.message || ''}
                  endIcon={
                    <img
                      src={
                        showPassword.againPassword
                          ? SVG.eyeOnIcon
                          : SVG.eyeOffIcon
                      }
                      alt="password-strength"
                      className="h-7 w-7 mb-1"
                      onClick={() =>
                        setShowPassword(prev => ({
                          ...prev,
                          againPassword: !prev.againPassword,
                        }))
                      }
                    />
                  }
                />
              </div>
            );
          }}
        />
        <div className="flex justify-start gap-2 flex-row-reverse w-full ">
          <AppButton
            onClick={handleSubmit(handleConfirm)}
            variant="primary"
            className="mt-6 !py-1 h-10 text-center flex items-center justify-center"
            disabled={
              isPasswordChanging ||
              oldPassword === '' ||
              newPassword === '' ||
              againPassword === ''
            }
            isLoading={isPasswordChanging}>
            שמירה
          </AppButton>
          <AppButton
            onClick={onClose}
            className="mt-6 !py-1 h-10 text-center flex items-center justify-center">
            ביטול
          </AppButton>
        </div>
      </div>
    </AppDialog>
  );
}

export default AppChangePasswordDialog;
